import { useState, useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router";
import { Grid, Typography, Button } from "@mui/material";
import { SortingState } from "@tanstack/react-table";
import { useQuery } from "react-query";
import CustomerTable from "../CustomerTable";
import UserModal from "../../order_management/orderedit_components/UserModal";
import AppStateContext from "../../../contexts/AppStateContext";
import { UserType } from "types/users/UserTypes";
import useStateWithSessionStorage from "hooks/UseStateWithSessionStorage";
import useSearchQueryParam from "hooks/UseSearchQueryParam";
import { useToast } from "contexts/ToastContext";
import { apiGetCustomers, apiGetUser } from "../../../util/network/Users";
import { apiGetCustomersReport } from "../../../util/network/Overview";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchField from "components/SearchField";

const CustomerManagementUI = () => {
  const { token, setShowUserModal } = useContext(AppStateContext);
  const { addToast } = useToast();
  const { userId } = useParams<{ userId?: string }>();
  const history = useHistory();
  const [existing, setExisting] = useState<UserType | null>();
  const [searchVal, setSearchVal] = useStateWithSessionStorage<string>("", "customerSearchVal");
  const [page, setPage] = useStateWithSessionStorage<number>(0, "customerPage");
  const [pageSize, setPageSize] = useStateWithSessionStorage<number>(10, "customerPageSize");
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    if (searchVal) setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVal]);

  // Lets global search's "See all" hand this page a term (EE-222).
  // SearchField syncs its own box from `value`, so the committed term is
  // the only thing that has to move.
  useSearchQueryParam(setSearchVal);

  // /kunder/:userId - deep link to one customer, used by the global search
  // (EE-222). There is no separate detail page; the customer "detail view"
  // is the edit modal, so fetch that user and open it, then drop back to
  // the plain list URL so a refresh doesn't reopen the modal forever.
  useEffect(() => {
    if (!token || !userId) return;
    const id = Number(userId);
    if (Number.isNaN(id)) {
      history.replace("/kunder");
      return;
    }
    let cancelled = false;
    apiGetUser(token, id)
      .then((user) => {
        if (cancelled) return;
        setExisting(user);
        setShowUserModal(true);
      })
      .catch((err) => {
        // Without this the user lands back on the plain list with no modal
        // and no explanation - a deleted/renumbered id or a permission error
        // looks identical to the click having done nothing.
        console.error("Failed to load customer:", err);
        if (cancelled) return;
        addToast({ type: "error", message: "Could not open that customer." });
      })
      .finally(() => {
        if (!cancelled) history.replace("/kunder");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]);

  const sortKey = sorting[0]?.id;
  const sortAsc = sorting[0] ? !sorting[0].desc : false;

  const { data, isFetching, refetch } = useQuery(
    ["customers", page, pageSize, sortKey, sortAsc, searchVal],
    () => {
      if (!token) return { items: [] as UserType[], total: 0 };
      // One endpoint whether or not a term is typed, so paging, sorting and
      // the reported total keep working while searching (EE-222).
      return apiGetCustomers(
        token,
        page + 1,
        pageSize,
        sortKey,
        sortAsc ? "asc" : "desc",
        searchVal
      );
    },
    { keepPreviousData: true, enabled: !!token }
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            paddingRight: "10px",
            background: "var(--header)",
            color: "#fff",
            height: "6vh",
            paddingTop: "20px",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", paddingRight: "10px" }}>
            <Typography
              style={{ textTransform: "uppercase", color: "#fff", paddingLeft: "1rem" }}
              fontWeight="bold"
              variant="h6"
            >
              &nbsp;Customer overview
            </Typography>
          </div>
          <div style={{ display: "flex" }}>
            <div style={{ paddingRight: "1rem" }}>
              <SearchField
                value={searchVal}
                onChange={setSearchVal}
                placeholder="Søg i kunder.."
                help="customer"
              />
            </div>
            <div style={{ paddingRight: "0.5rem", display: "flex", gap: "0.5rem" }}>
              <Button
                onClick={() => token && apiGetCustomersReport(token)}
                variant="contained"
                startIcon={<FileDownloadIcon />}
                sx={{
                  borderRadius: 0,
                  maxHeight: 40,
                  background: "#E0A900",
                  lineHeight: 2,
                  color: "#fff",
                  "&:hover": {
                    color: "#E0A900",
                    border: "1px solid #E0A900",
                    background: "transparent",
                  },
                }}
              >
                Download Report
              </Button>
              <Button
                sx={{ borderRadius: 0, maxHeight: 40, width: "10rem", background: "#E0A900" }}
                onClick={() => setShowUserModal(true)}
                variant="contained"
              >
                <h4 style={{ color: "#fff" }}>Create new</h4>
              </Button>
              <UserModal
                setCustomer={setExisting}
                existing={existing}
                onSubmit={() => {
                  setExisting(null);
                  setTimeout(() => refetch(), 2000);
                }}
                onClose={() => setExisting(null)}
              />
            </div>
          </div>
        </div>
      </Grid>
      <Grid item xs={12} style={{ padding: "2rem 1.5rem 0 1.5rem" }}>
        <CustomerTable
          setExisting={setExisting}
          users={data?.items ?? []}
          total={data?.total ?? 0}
          isFetching={isFetching}
          page={page}
          pageSize={pageSize}
          sorting={sorting}
          onSortingChange={setSorting}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onRefresh={refetch}
        />
      </Grid>
    </Grid>
  );
};

export default CustomerManagementUI;
